import { isRecoveryActionEvent, isResolutionChangeStatus, isPlaybackCapacityDecision, isMobileTierDecision } from './streamRecovery/validators';
import type { DiagnosticEnvelope } from './streamReliability/contracts/diagnostics';
import type { StreamControlMessage } from './streamReliability/contracts/operatingPoint';
import {
  isDiagnosticEnvelope,
  isStreamControlMessage,
} from './streamReliability/contracts/validators';

/**
 * Unified stream-health observability contract.
 *
 * One structured, schema-versioned record of how every real-time stream in a session is
 * doing — video, audio, input, and signaling — plus the ordered timeline of things that
 * happened to them. This is the observability backbone the whole feature hangs on: the
 * guest produces snapshots (Phase 2), the host aggregates + persists them (Phase 3), and the
 * decision layers emit events into the same timeline (Phase 6).
 *
 * Browser-free and Node/Jest-safe: types, pure functions, and structural guards only. No
 * transport, no DOM, no `window`.
 *
 * Measurement philosophy (carried over from clientStreamStatsContracts): a field that could
 * not be measured is OMITTED, never zero-filled — a fabricated 0 is indistinguishable from a
 * genuine healthy reading downstream.
 */

export const STREAM_HEALTH_SCHEMA_VERSION = 1;
export const STREAM_HEALTH_V2_SCHEMA_VERSION = 2;
export const STREAM_HEALTH_MESSAGE_TYPE = 'streamHealth' as const;

export type StreamKind = 'video' | 'audio' | 'input' | 'signaling';

/** One stream's most recent measured health. Undefined fields = not measured this tick. */
export type StreamHealthSnapshot = {
  kind: StreamKind;
  rttMs?: number;
  packetsLostPct?: number;
  /** video: decode fps; audio: audio frame cadence; else omitted. */
  fps?: number;
  bitrateBps?: number;
  /** glass-to-glass / calibrated latency where measurable (video, input). */
  latencyMs?: number;
  /** freeze/backlog/stall indicator for the kind. */
  stallScore?: number;
  atMs: number;
};

/** A single thing that happened to a stream, in session order. */
export type StreamHealthEventKind =
  | 'sample'
  | 'downgrade'
  | 'latency-budget-breach'
  /**
   * The link's own latency floor exceeds the glass-to-glass budget: the guest is far from
   * the host. Observability only — distance is not fault, so it is never acted on.
   */
  | 'latency-floor-over-budget'
  /**
   * The guest's decoder is holding frames it has received but not emitted — the player is
   * waiting behind a decode backlog, not a network queue. Emitted once when a backup begins
   * and once when it clears (`detail.phase`), with the depth, the decode rate against the
   * encoder's target, and the classification (throughput deficit vs standing depth).
   */
  | 'decoder-backup'
  /**
   * The glass-to-glass floor is far above what the route and decoder account for while the
   * trend is flat, the queue empty and loss nil: frames are standing in the receive-side
   * buffer. Unlike `latency-floor-over-budget` this IS acted on (drain). One at begin, one
   * `standing-buffer-cleared` at end, with depth and the depth hypothesis in `detail`.
   */
  | 'standing-buffer'
  | 'standing-buffer-cleared'
  /** One drain step taken against a standing buffer: action, read-back, before/after, outcome. */
  | 'drain'
  /** A sticky floor released one rung on measured evidence (D1 amended): from, to, reason. */
  | 'reclaim'
  /** The guest's decoded frame size changed: before/after size and standing depth. */
  | 'resolution-switch'
  | 'transport-lost'
  | 'transport-resumed'
  /**
   * No `clientStats` message from a peer that previously sent them for
   * `CLIENT_STATS_STALE_AFTER_MS`. Emitted once at the healthy→stale edge; guest-sourced
   * evidence is degraded to insufficient-data while stale.
   */
  | 'client-stats-stale'
  /**
   * The host observed a `seq` jump between consecutive `clientStats` messages: messages were
   * dropped in transit, not a guest stall. Observability only — no ladder-state change.
   */
  | 'client-stats-gap'
  | 'network-path-changed'
  | 'measurement-stale'
  | 'presentation-stall'
  | 'presentation-resumed'
  | 'recovery-action'
  | 'codec-implementation'
  | 'mobile-tier-selected'
  | 'quality-tier-decision'
  | 'quality-change-stage'
  /**
   * The host's send-codec hardware pin decision: which codec (if any) was pinned, the full
   * negotiated set considered, which were hardware at BOTH ends, and which the host encodes
   * in hardware but the guest decodes in software. Always-on so a field session answers
   * "was this stream on hardware HEVC or software H.264" without a debug build.
   */
  | 'send-codec-decision'
  | 'session-end';

export type StreamHealthEvent = {
  kind: StreamHealthEventKind;
  stream: StreamKind;
  atMs: number;
  snapshot?: StreamHealthSnapshot;
  detail?: Record<string, number | string | boolean>;
};

export type SessionHealthRecord = {
  sessionId: string;
  peerId: string;
  startedAtMs: number;
  /** Latest snapshot per stream kind. */
  streams: StreamHealthSnapshot[];
  /** Bounded ordered timeline. */
  events: StreamHealthEvent[];
};

export type StreamHealthMessage = {
  type: typeof STREAM_HEALTH_MESSAGE_TYPE;
  version: typeof STREAM_HEALTH_SCHEMA_VERSION;
  peerId: string;
  snapshots: StreamHealthSnapshot[];
};

export type StreamHealthV2Payload =
  | { kind: 'diagnostic'; diagnostic: DiagnosticEnvelope }
  | { kind: 'control'; control: StreamControlMessage };

/** V2 carries one bounded record so ingress can reject it before policy or allocation. */
export type StreamHealthV2Message = {
  type: typeof STREAM_HEALTH_MESSAGE_TYPE;
  version: typeof STREAM_HEALTH_V2_SCHEMA_VERSION;
  peerId: string;
  payload: StreamHealthV2Payload;
};

export type StreamHealthIngressMessage = StreamHealthMessage | StreamHealthV2Message;

const STREAM_KINDS: ReadonlyArray<StreamKind> = ['video', 'audio', 'input', 'signaling'];
const EVENT_KINDS: ReadonlyArray<StreamHealthEventKind> = [
  'sample',
  'downgrade',
  'latency-budget-breach',
  'latency-floor-over-budget',
  'decoder-backup',
  'standing-buffer',
  'standing-buffer-cleared',
  'drain',
  'reclaim',
  'resolution-switch',
  'transport-lost',
  'transport-resumed',
  'client-stats-stale',
  'client-stats-gap',
  'network-path-changed',
  'measurement-stale',
  'presentation-stall',
  'presentation-resumed',
  'recovery-action',
  'codec-implementation',
  'mobile-tier-selected',
  'quality-tier-decision',
  'quality-change-stage',
  'send-codec-decision',
  'session-end',
];

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isStreamKind(v: unknown): v is StreamKind {
  return typeof v === 'string' && (STREAM_KINDS as ReadonlyArray<string>).includes(v);
}

function isOptionalFinite(v: unknown): boolean {
  return v === undefined || isFiniteNumber(v);
}

export function isStreamHealthSnapshot(value: unknown): value is StreamHealthSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  if (!isStreamKind(s['kind'])) return false;
  if (!isFiniteNumber(s['atMs'])) return false;
  return (
    isOptionalFinite(s['rttMs']) &&
    isOptionalFinite(s['packetsLostPct']) &&
    isOptionalFinite(s['fps']) &&
    isOptionalFinite(s['bitrateBps']) &&
    isOptionalFinite(s['latencyMs']) &&
    isOptionalFinite(s['stallScore'])
  );
}

export function isStreamHealthEvent(value: unknown): value is StreamHealthEvent {
  if (value === null || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  if (!isStreamKind(e['stream'])) return false;
  if (!isFiniteNumber(e['atMs'])) return false;
  if (!(EVENT_KINDS as ReadonlyArray<string>).includes(e['kind'] as string)) return false;
  if (e['snapshot'] !== undefined && !isStreamHealthSnapshot(e['snapshot'])) return false;
  const detail = e['detail'];
  if (detail !== undefined) {
    if (detail === null || typeof detail !== 'object' || Array.isArray(detail)) return false;
    for (const v of Object.values(detail as Record<string, unknown>)) {
      const t = typeof v;
      if (t !== 'number' && t !== 'string' && t !== 'boolean') return false;
      if (t === 'number' && !Number.isFinite(v)) return false;
    }
  }
  return isRecoveryHealthDetail(e['kind'], detail);
}

export function isStreamHealthMessage(value: unknown): value is StreamHealthMessage {
  if (value === null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  if (m['type'] !== STREAM_HEALTH_MESSAGE_TYPE) return false;
  if (m['version'] !== STREAM_HEALTH_SCHEMA_VERSION) return false;
  if (typeof m['peerId'] !== 'string' || m['peerId'].length === 0) return false;
  const snapshots = m['snapshots'];
  if (!Array.isArray(snapshots)) return false;
  return snapshots.every(isStreamHealthSnapshot);
}

export function isStreamHealthV2Message(value: unknown): value is StreamHealthV2Message {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const message = value as Record<string, unknown>;
  if (message['type'] !== STREAM_HEALTH_MESSAGE_TYPE
    || message['version'] !== STREAM_HEALTH_V2_SCHEMA_VERSION
    || typeof message['peerId'] !== 'string' || message['peerId'].length === 0
    || message['peerId'].length > 256) return false;
  const payload = message['payload'];
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const fields = payload as Record<string, unknown>;
  if (fields['kind'] === 'diagnostic') return isDiagnosticEnvelope(fields['diagnostic']);
  if (fields['kind'] === 'control') return isStreamControlMessage(fields['control']);
  return false;
}

/** Explicit compatibility bridge; legacy callers keep using the v1-only guard above. */
export function isStreamHealthIngressMessage(value: unknown): value is StreamHealthIngressMessage {
  return isStreamHealthMessage(value) || isStreamHealthV2Message(value);
}

/** New event details are bounded; legacy event shapes retain their prior behavior. */
function isRecoveryHealthDetail(kind: unknown, detail: unknown): boolean {
  const recoveryKinds = ['network-path-changed', 'measurement-stale', 'presentation-stall',
    'presentation-resumed', 'recovery-action', 'codec-implementation', 'mobile-tier-selected',
    'quality-tier-decision', 'quality-change-stage'];
  if (!recoveryKinds.includes(kind as string)) return true;
  if (detail === null || typeof detail !== 'object' || Array.isArray(detail)) return false;
  const fields = detail as Record<string, unknown>;
  if (Object.keys(fields).length > 24) return false;
  if (!Object.entries(fields).every(([key, value]) => key.length <= 64 &&
    (typeof value !== 'string' || value.length <= (key === 'implementation' ? 128 : 160)) &&
    (typeof value !== 'number' || value >= 0))) return false;
  if (kind === 'recovery-action') return isRecoveryActionEvent(fields);
  if (kind === 'quality-change-stage') return isResolutionChangeStatus(fields);
  if (kind === 'quality-tier-decision') return isPlaybackCapacityDecision(fields);
  if (kind === 'mobile-tier-selected') {
    const { device, tier, maxWidth, maxHeight, reason } = fields;
    return isMobileTierDecision({ device, tier, maxWidth, maxHeight, reason });
  }
  return true;
}
